import React, { useEffect, useState } from 'react'
import { useIntl } from 'react-intl'
import { useLocation, useParams, useSearchParams } from 'react-router-dom'
import styled from '@emotion/styled'
import { Box, Button, Chip, IconButton, LinearProgress } from '@mui/material'
import { curatorStudentsApiSlice } from '../../../features/curator/curatorStudents'
import Breadcrumb from '../../../components/Breadcrumb'
import { TEST, VIDEO, HOMEWORK, VIDEONOTES } from '../../../utils/constants'
import VideoTab from './videoTab/VideoTab'
import TestTab from './testTab/TestTab'
import HomeworkTab from './homeworkTab/HomeworkTab'
import VideonotesTab from './videonotesTab/VideonotesTab'
import SingleSelect from '../../../components/muiComponents/singleSelect/SingleSelect'
import userIcon from '../../../assets/img/avatar.jpg'
import { H3, Body1 } from '../../../layouts/TextStyles'
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import { CuratorStudentState } from '../../../features/types'
import { capitalize } from '../../../utils/functions'
import HistoryIcon from '@mui/icons-material/History';
import VerifiedOutlinedIcon from '@mui/icons-material/VerifiedOutlined';
import AssignmentOutlinedIcon from '@mui/icons-material/AssignmentOutlined';
import { toast } from 'react-toastify'

const StudentProgressPage = () => {
    const { formatMessage, locale } = useIntl()
    const { state } = useLocation()
    const { userCourseEnrollId } = useParams()
    const [subjectId, setSubjectId] = useState<number | null>(null)
    const [searchParams, setSearchParams] = useSearchParams(new URLSearchParams('type=VIDEO'))
    const { data: studentProgress = {} as CuratorStudentState, isFetching: loadingStudentProgress, refetch: refetchStudentProgress } =
        curatorStudentsApiSlice.useFetchStudentProgressQuery({ userCourseEnrollId: +userCourseEnrollId })
    const [updateStudentPerformance, { isLoading: loadingUpdateStudentPerformance }] = curatorStudentsApiSlice.useUpdateStudentPerformanceMutation()
    const urlFullName = searchParams.get('fullName')
    const fullName = state?.student
        ? state.student.firstName + ' ' + state.student.lastName
        : urlFullName
            ? decodeURIComponent(urlFullName).replace(/-/g, ' ')
            : ''

    const handlePerformanceLevelChange = async (e: React.ChangeEvent<HTMLSelectElement>) => {
        const newPerformanceLevel = e.target.value
        try {
            await updateStudentPerformance({ userCourseEnrollId: +userCourseEnrollId, performanceLevel: newPerformanceLevel }).unwrap()
            await refetchStudentProgress()
            toast.success(formatMessage({ id: 'performanceLevelUpdated' }))
        } catch (error) {
            console.error(error)
            toast.error(formatMessage({ id: 'error' }))
        }
    }

    const handleTabChange = (type: string) => {
        const p = new URLSearchParams(searchParams);
        p.set('type', type);
        setSearchParams(p, { replace: true });
    };

    const handleSubjectClick = (subjectId: number) => {
        setSubjectId(subjectId)
    }

    const getDuration = (daysLeft: number) => {
        if (daysLeft > 30) {
            return (
                <CourseInfoSpan color="#E62D95" backgroundColor="#FFECF7">
                    <Medium color="#E62D95">
                        {Math.floor(daysLeft / 30) + ' ' + formatMessage({ id: 'month' })}
                    </Medium>
                    <DayLabel>
                        <HistoryIcon sx={{ fontSize: 18 }} />
                        {daysLeft % 30 + ' ' + formatMessage({ id: 'day' })}
                    </DayLabel>
                </CourseInfoSpan>)
        } else if (daysLeft === 0) {
            return <FinishedLabel>✓ {formatMessage({ id: 'courseCompleted' })}</FinishedLabel>
        } else {
            return (
                <CourseInfoSpan color="#E62D95" backgroundColor="#FFECF7">
                    <DayLabel>
                        <HistoryIcon sx={{ fontSize: 18 }} />
                        {daysLeft + ' ' + formatMessage({ id: 'day' })}
                    </DayLabel>
                </CourseInfoSpan>
            )
        }
    }

    useEffect(() => {
        if (studentProgress?.subjects?.length && !subjectId) {
            setSubjectId(studentProgress?.subjects[0].id)
        }
    }, [studentProgress?.subjects, subjectId])


    return (
        <Wrapper>
            <Box sx={styles.boxContainer}>
                <Breadcrumb
                    links={[
                        formatMessage({ id: 'groups' }),
                        formatMessage({ id: 'student' }),
                        fullName
                    ]}
                />
            </Box>

            {loadingStudentProgress ? (
                <LinearProgress />
            ) : (
                <Content>
                    <StudentCard>
                        <Box>
                            <img src={userIcon} alt="user" width={140} height={132} style={{ borderRadius: '24px' }} />
                        </Box>
                        <Box sx={{ ...styles.studentInfo, marginLeft: '20px', marginRight: 'auto' }}>
                            <H3>{studentProgress?.firstName} {studentProgress?.lastName}</H3>
                            <Body1>{formatMessage({ id: 'birthDate' })}{' : '}<span style={{ fontWeight: 600, color: '#0D894F' }}>{studentProgress?.birthDate}</span></Body1>
                            <Body1>{formatMessage({ id: 'mobileNumber' })}{' : '}<span style={{ fontWeight: 600, color: '#0D894F' }}>{studentProgress?.mobile}</span></Body1>
                            <Body1>{formatMessage({ id: 'parentMobileNumber' })}{' : '}<span style={{ fontWeight: 600, color: '#0D894F' }}>{studentProgress?.parentMobile}</span></Body1>
                        </Box>
                        <Box sx={{ ...styles.studentInfo, alignItems: 'flex-end' }}>
                            <Button variant="contained" color="primary" startIcon={<AssignmentOutlinedIcon sx={{ fontSize: 18 }} />} disabled>
                                {formatMessage({ id: 'note' })}
                            </Button>
                            <StatusSelect
                                value={studentProgress?.performanceLevel || formatMessage({ id: 'performance' })}
                                performanceLevel={studentProgress?.performanceLevel || ''}
                                onChange={handlePerformanceLevelChange}
                                disabled={loadingUpdateStudentPerformance}>
                                <option disabled selected value={formatMessage({ id: 'performance' })}>{formatMessage({ id: 'performance' })}</option>
                                {studentProgress?.performances?.map((performance) => (
                                    <option key={performance} value={performance}>{formatMessage({ id: `${performance}` })}</option>
                                ))}
                            </StatusSelect>
                            <IconButton sx={styles.editIcon} disabled><EditOutlinedIcon /></IconButton>
                        </Box>
                    </StudentCard>
                    <CourseInfoCard>
                        <Box>
                            <Medium color='#333843'>{formatMessage({ id: 'courseInfo' })}</Medium>
                            <Box sx={styles.courseInfoBox}>
                                <CourseInfoSpan color="#0D894F" backgroundColor="#E7F4EE">
                                    <VerifiedOutlinedIcon sx={{ fontSize: 18 }} />
                                    {studentProgress?.[`coursesTitle${capitalize(locale)}`]}
                                </CourseInfoSpan>
                                {studentProgress?.daysLeft !== undefined && (
                                    getDuration(studentProgress?.daysLeft)
                                )}

                            </Box>
                        </Box>
                        <Box>
                            <Medium color='#333843'>{formatMessage({ id: 'subjects' })}</Medium>
                            <Box sx={styles.courseInfoBox}>
                                {(studentProgress?.subjects || []).map((subject) => (
                                    <Chip key={subject.id}
                                        label={subject?.[`title${capitalize(locale)}`]}
                                        sx={{
                                            backgroundColor: subjectId === subject.id && '#EFEFFD',
                                            color: subjectId === subject.id && '#5C59E8'
                                        }}
                                        onClick={() => handleSubjectClick(subject.id)}
                                    />
                                ))}
                            </Box>
                        </Box>

                    </CourseInfoCard>

                    <Box sx={{ flexDirection: 'column', display: 'flex', gridColumn: '1 / 3' }}>
                        <Tabs>
                            <Tab
                                searchParams={searchParams.get('type')}
                                type={VIDEO}
                                onClick={() => handleTabChange(VIDEO)}
                            >
                                {formatMessage({ id: 'video' })}
                            </Tab>

                            <Tab
                                searchParams={searchParams.get('type')}
                                type={TEST}
                                onClick={() => handleTabChange(TEST)}
                            >
                                {formatMessage({ id: 'test' })}
                            </Tab>

                            <Tab
                                searchParams={searchParams.get('type')}
                                type={HOMEWORK}
                                onClick={() => handleTabChange(HOMEWORK)}
                            >
                                {formatMessage({ id: 'homework' })}
                            </Tab>

                            <Tab
                                searchParams={searchParams.get('type')}
                                type={VIDEONOTES}
                                onClick={() => handleTabChange(VIDEONOTES)}
                            >
                                {formatMessage({ id: 'videonotes' })}
                            </Tab>
                        </Tabs>
                        {searchParams.get('type') === TEST && studentProgress?.userId && (
                            <TestTab
                                studentId={+studentProgress?.userId}
                                subjectId={subjectId}
                                loadingSubjects={loadingStudentProgress}
                            />
                        )}
                        {searchParams.get('type') === VIDEO && studentProgress?.userId && (
                            <VideoTab
                                studentId={+studentProgress?.userId}
                                subjectId={subjectId}
                                loadingSubjects={loadingStudentProgress}
                            />
                        )}
                        {searchParams.get('type') === HOMEWORK && (
                            <HomeworkTab
                                userCourseEnrollId={+userCourseEnrollId}
                                subjectId={subjectId}
                                loadingSubjects={loadingStudentProgress}
                            />
                        )}
                        {searchParams.get('type') === VIDEONOTES && (
                            <VideonotesTab
                                userCourseEnrollId={+userCourseEnrollId}
                                subjectId={subjectId}
                                loadingSubjects={loadingStudentProgress}
                            />
                        )}
                    </Box>
                </Content>
            )}
        </Wrapper>
    )
}

export default StudentProgressPage

const Wrapper = styled.div`
    margin: 10px;
    border-radius: 16px;
`

const Content = styled.div`
    display: grid;
    grid-template-columns: 2fr 1fr;
    grid-template-rows: max-content 1fr;
    gap: 16px;
    background-color: #fff;
    border-radius: 16px;
    padding: 20px;
    min-height: 80vh;
`

const Tabs = styled.div`
    margin-left: auto;
    display: flex;
    background: #F1F3F5;
    height: 24px;
    padding: 4px;
    border-radius: 8px;
    align-items: center;
    margin-bottom: 16px;
    cursor: pointer;
`

const Tab = styled.div<{ searchParams: string; type: string }>`
    background: ${(props) =>
        props.type == VIDEO && props.searchParams == null
            ? '#FFFFFF'
            : props.searchParams == props.type
                ? '#FFFFFF'
                : '#E4E5E7'};
    border-radius: 5px;
    padding: 4px 20px;
    color: ${(props) =>
        props.type == VIDEO && props.searchParams == null
            ? '#404A51'
            : props.searchParams == props.type
                ? '#404A51'
                : '#AABBC7'}; ;
`

const StudentCard = styled.div`
    display: flex;
    align-items: center;
    padding: 27px 16px;
    border-radius: 16px;
    background-color: #F1F3F5;
`

const CourseInfoCard = styled.div`
    display: flex;
    flex-direction: column;
    justify-content: space-between;
    padding: 16px;
    border-radius: 16px;
    background-color: #F1F3F5;
`

const CourseInfoSpan = styled.span<{ color: string, backgroundColor: string }>`
    color: ${({ color }) => color};
    background-color: ${({ backgroundColor }) => backgroundColor};
    padding: 6px 12px;
    border-radius: 24px;
    font-weight: 600;
    font-size: 16px;
    display: flex;
    text-align: center;
    gap: 8px;
`

const StatusSelect = styled.select<{ performanceLevel: string }>`
    background-color: ${({ performanceLevel }) => {
        switch (performanceLevel) {
            case 'ACTIVE':
                return '#E7F4EE';
            case 'MEDIUM':
                return '#E8F8FD';
            case 'LOW':
                return '#FDF1E8';
            case 'NEEDS_HELP':
                return '#FEEDEC';
            default:
                return '#E0E2E7';
        }
    }};
    color: ${({ performanceLevel }) => {
        switch (performanceLevel) {
            case 'ACTIVE':
                return '#1B998B';
            case 'MEDIUM':
                return '#13B2E4';
            case 'LOW':
                return '#E46A11';
            case 'NEEDS_HELP':
                return '#F04438';
            default:
                return '#434343';
        }
    }};
    border-radius: 100px;
    padding: 6px 0;
    text-align: center;
    font-weight: 600;
    font-size: 14px;
    border: none;
    outline: none;
    cursor: pointer;
`

const Medium = styled.span<{ color: string }>`
    font-size: 16px;
    font-weight: 600;
    color: ${({ color }) => color};
`

const DayLabel = styled.span`
    font-size: 14px;
    color: #667085;
    display: flex;
    align-items: center;
    gap: 2px;
`

const FinishedLabel = styled.span`
    color: #0D894F;
    font-weight: 500;
    font-size: 16px;
    background-color: #CFE7DC;
    border: 4px solid #E7F4EE;
    border-radius: 100px;
    padding: 2px 12px;
`

const styles = {
    boxContainer: {
        display: 'flex',
        flexDirection: 'row',
        justifyContent: 'space-between',
        mb: 1,
    },
    studentInfo: {
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'space-between',
        height: '100%',
    },
    courseInfoBox: {
        marginTop: '8px',
        display: 'flex',
        flexWrap: 'wrap',
        gap: '8px',
    },
    editIcon: {
        background: '#EFEFFD',
        borderRadius: '8px',
        color: '#4D60D7',
        width: '42px',
        height: '42px',
    },
}